import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { createVessel } from '../../firebase/services/vesselService';
import { Vessel } from '../../models/types';

const AddVesselPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [vessel, setVessel] = useState<Omit<Vessel, 'id'>>({
    name: '',
    type: '',
    propulsionType: 'powered',
    capacity: 0,
    description: '',
    isActive: true,
    status: 'active',
    images: [],
    amenities: [],
    manufacturer: '',
    registrationNumber: '',
    datePlacedInService: '',
    engineHorsepower: 0,
    engineSerialNumber: '',
    length: 0,
    year: 0,
    model: '',
    stateRegistrationExpiry: '',
    stateRegistrationState: '',
  });

  // Set initial propulsion type from URL parameters
  useEffect(() => {
    const propulsionTypeParam = searchParams.get('propulsionType');
    if (propulsionTypeParam === 'powered' || propulsionTypeParam === 'non-powered') {
      setVessel((prev) => ({
        ...prev,
        propulsionType: propulsionTypeParam,
      }));
    }
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setVessel((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    
    // If changing propulsion type, clear related fields
    if (name === 'propulsionType') {
      if (value === 'non-powered') {
        // Clear registration, engine, and type fields for non-powered vessels
        setVessel((prev) => ({
          ...prev,
          propulsionType: 'non-powered' as const,
          type: '', // Clear vessel type since options change
          registrationNumber: '',
          stateRegistrationExpiry: '',
          stateRegistrationState: '',
          engineHorsepower: 0,
          engineSerialNumber: '',
        }));
      } else {
        // Clear type field for powered vessels too
        setVessel((prev) => ({
          ...prev,
          propulsionType: 'powered' as const,
          type: '', // Clear vessel type since options change
        }));
      }
    } else {
      setVessel((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createVessel(vessel);
      // Navigate to the appropriate vessels page based on propulsion type
      if (vessel.propulsionType === 'powered') {
        navigate('/admin/equipment/vessels/powered');
      } else {
        navigate('/admin/equipment/vessels/nonpowered');
      }
    } catch (error) {
      console.error('Error creating vessel:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Add New Vessel
        </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vessel Name"
                  name="name"
                  value={vessel.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Propulsion Type</InputLabel>
                  <Select
                    name="propulsionType"
                    value={vessel.propulsionType}
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="powered">Powered</MenuItem>
                    <MenuItem value="non-powered">Non-Powered</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Vessel Type</InputLabel>
                  <Select
                    name="type"
                    value={vessel.type}
                    onChange={handleSelectChange}
                  >
                    {vessel.propulsionType === 'powered' ? (
                      // Powered vessel types
                      <>
                        <MenuItem value="pontoon">Pontoon</MenuItem>
                        <MenuItem value="fishing boat">Fishing Boat</MenuItem>
                        <MenuItem value="speedboat">Speedboat</MenuItem>
                        <MenuItem value="jet ski">Jet Ski</MenuItem>
                        <MenuItem value="cabin cruiser">Cabin Cruiser</MenuItem>
                        <MenuItem value="ski boat">Ski Boat</MenuItem>
                        <MenuItem value="center console">Center Console</MenuItem>
                      </>
                    ) : (
                      // Non-powered vessel types
                      <>
                        <MenuItem value="kayak">Kayak</MenuItem>
                        <MenuItem value="canoe">Canoe</MenuItem>
                        <MenuItem value="paddleboard">Paddleboard (SUP)</MenuItem>
                        <MenuItem value="rowboat">Rowboat</MenuItem>
                        <MenuItem value="pedal boat">Pedal Boat</MenuItem>
                        <MenuItem value="raft">Raft</MenuItem>
                      </>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Capacity (persons)"
                  name="capacity"
                  type="number"
                  value={vessel.capacity === 0 ? '' : vessel.capacity}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  multiline
                  rows={3}
                  value={vessel.description}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Manufacturer"
                  name="manufacturer"
                  value={vessel.manufacturer}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Model"
                  name="model"
                  value={vessel.model}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Year"
                  name="year"
                  type="number"
                  value={vessel.year === 0 ? '' : vessel.year}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Length (feet)"
                  name="length"
                  type="number"
                  value={vessel.length === 0 ? '' : vessel.length}
                  onChange={handleChange}
                />
              </Grid>
              {vessel.propulsionType === 'powered' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Registration Information
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Registration Number"
                      name="registrationNumber"
                      value={vessel.registrationNumber}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="State Registration Expiry"
                      name="stateRegistrationExpiry"
                      type="date"
                      value={vessel.stateRegistrationExpiry}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Registration State</InputLabel>
                      <Select
                        name="stateRegistrationState"
                        value={vessel.stateRegistrationState}
                        onChange={handleSelectChange}
                      >
                        {/* Add state options here */}
                        <MenuItem value="FL">Florida</MenuItem>
                        <MenuItem value="GA">Georgia</MenuItem>
                        <MenuItem value="SC">South Carolina</MenuItem>
                        {/* Add more states as needed */}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date Placed in Service"
                  name="datePlacedInService"
                  type="date"
                  value={vessel.datePlacedInService}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              {vessel.propulsionType === 'powered' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Engine Information
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Engine Horsepower"
                      name="engineHorsepower"
                      type="number"
                      value={vessel.engineHorsepower === 0 ? '' : vessel.engineHorsepower}
                      onChange={handleChange}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Engine Serial Number"
                      name="engineSerialNumber"
                      value={vessel.engineSerialNumber}
                      onChange={handleChange}
                    />
                  </Grid>
                </>
              )}
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="button"
                onClick={() => {
                  // Navigate back to the appropriate vessels page
                  if (vessel.propulsionType === 'powered') {
                    navigate('/admin/equipment/vessels/powered');
                  } else {
                    navigate('/admin/equipment/vessels/nonpowered');
                  }
                }}
                sx={{ mr: 2 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Add Vessel'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    );
};

export default AddVesselPage; 